'use client';

import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type FramingMode = 'crop' | 'fit' | 'fill';
type BackgroundPreset =
  | 'transparent'
  | 'white'
  | 'black'
  | 'charcoal'
  | 'light'
  | 'cream'
  | 'custom';
type SocialMediaImageResizerVariant = {
  lockedPresetId?: string;
  hidePresetChooser?: boolean;
  badgeLabel?: string;
  toolSlug?: string;
};

type SocialPreset = {
  id: string;
  label: string;
  width: number;
  height: number;
  hint: string;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

const presets: SocialPreset[] = [
  {
    id: 'instagram-post',
    label: 'Instagram Post',
    width: 1080,
    height: 1080,
    hint: 'Square framing for feed posts, launches, quotes, and simple product cards.',
  },
  {
    id: 'instagram-story',
    label: 'Instagram Story',
    width: 1080,
    height: 1920,
    hint: 'Tall mobile format for stories, promos, and vertical announcements.',
  },
  {
    id: 'facebook-post',
    label: 'Facebook Post',
    width: 1200,
    height: 630,
    hint: 'A wide social share size that also maps cleanly to many link previews.',
  },
  {
    id: 'x-post',
    label: 'X Post',
    width: 1600,
    height: 900,
    hint: 'A broad landscape frame for posts, updates, screenshots, and promo art.',
  },
  {
    id: 'linkedin-post',
    label: 'LinkedIn Post',
    width: 1200,
    height: 627,
    hint: 'Balanced for professional updates, carousels, launch graphics, and hiring posts.',
  },
  {
    id: 'linkedin-banner',
    label: 'LinkedIn Banner',
    width: 1584,
    height: 396,
    hint: 'A very wide header for profile banners, team pages, and brand messaging.',
  },
  {
    id: 'youtube-thumbnail',
    label: 'YouTube Thumbnail',
    width: 1280,
    height: 720,
    hint: 'The standard 16:9 thumbnail frame for videos, explainers, and tutorials.',
  },
  {
    id: 'tiktok-cover',
    label: 'TikTok Cover',
    width: 1080,
    height: 1920,
    hint: 'Vertical cover sizing for TikTok uploads and clean mobile-first framing.',
  },
];

const framingModes: Array<{
  value: FramingMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'crop',
    label: 'Smart crop',
    hint: 'Covers the whole preset and lets you drag the image to keep the right subject in frame.',
  },
  {
    value: 'fit',
    label: 'Fit',
    hint: 'Keeps the whole image visible and leaves padding only where the aspect ratio requires it.',
  },
  {
    value: 'fill',
    label: 'Fill',
    hint: 'Keeps the whole image visible and uses a solid background to fill the remaining space.',
  },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

const backgroundOptions: Array<{
  value: BackgroundPreset;
  label: string;
  color: string | null;
}> = [
  { value: 'transparent', label: 'Transparent', color: null },
  { value: 'white', label: 'White', color: '#ffffff' },
  { value: 'black', label: 'Black', color: '#000000' },
  { value: 'charcoal', label: 'Charcoal', color: '#111827' },
  { value: 'light', label: 'Soft gray', color: '#e5e7eb' },
  { value: 'cream', label: 'Warm ivory', color: '#f4efe6' },
  { value: 'custom', label: 'Custom', color: null },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function triggerDownload(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function getEffectiveBackground(
  preset: BackgroundPreset,
  customColor: string,
  outputFormat: OutputFormat,
) {
  if (outputFormat === 'jpeg' && preset === 'transparent') {
    return {
      label: 'White',
      color: '#ffffff',
      preset: 'white' as const,
    };
  }

  if (preset === 'custom') {
    return {
      label: 'Custom',
      color: customColor,
      preset,
    };
  }

  const option =
    backgroundOptions.find((background) => background.value === preset) ?? backgroundOptions[0];

  return {
    label: option.label,
    color: option.color,
    preset,
  };
}

function getPlacementMetrics(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  mode: FramingMode,
  zoom: number,
) {
  const scaleBase =
    mode === 'crop'
      ? Math.max(targetWidth / imageWidth, targetHeight / imageHeight)
      : Math.min(targetWidth / imageWidth, targetHeight / imageHeight);
  const scale = scaleBase * zoom;
  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;
  const baseX = (targetWidth - renderedWidth) / 2;
  const baseY = (targetHeight - renderedHeight) / 2;
  const minX = renderedWidth > targetWidth ? targetWidth - renderedWidth : 0;
  const maxX = renderedWidth > targetWidth ? 0 : targetWidth - renderedWidth;
  const minY = renderedHeight > targetHeight ? targetHeight - renderedHeight : 0;
  const maxY = renderedHeight > targetHeight ? 0 : targetHeight - renderedHeight;

  return {
    renderedWidth,
    renderedHeight,
    baseX,
    baseY,
    minX,
    maxX,
    minY,
    maxY,
  };
}

function clampOffsets(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  mode: FramingMode,
  zoom: number,
  offsetX: number,
  offsetY: number,
) {
  const metrics = getPlacementMetrics(
    imageWidth,
    imageHeight,
    targetWidth,
    targetHeight,
    mode,
    zoom,
  );

  return {
    x: clamp(offsetX, metrics.minX - metrics.baseX, metrics.maxX - metrics.baseX),
    y: clamp(offsetY, metrics.minY - metrics.baseY, metrics.maxY - metrics.baseY),
  };
}

function getSmartFraming(image: LoadedImage, preset: SocialPreset, mode: FramingMode) {
  if (mode !== 'crop') {
    return { zoom: 1, offsetX: 0, offsetY: 0 };
  }

  const metrics = getPlacementMetrics(
    image.width,
    image.height,
    preset.width,
    preset.height,
    mode,
    1,
  );
  const overflowY = Math.max(0, metrics.renderedHeight - preset.height);
  const nextOffsetY =
    image.height > image.width && overflowY > 0 ? -Math.min(overflowY * 0.14, overflowY / 2) : 0;

  return { zoom: 1, offsetX: 0, offsetY: nextOffsetY };
}

function drawResizedImage(
  context: CanvasRenderingContext2D,
  image: LoadedImage,
  targetWidth: number,
  targetHeight: number,
  mode: FramingMode,
  zoom: number,
  offsetX: number,
  offsetY: number,
  backgroundColor: string | null,
  outputFormat: OutputFormat,
) {
  context.clearRect(0, 0, targetWidth, targetHeight);

  if (mode === 'fill' || backgroundColor || outputFormat === 'jpeg') {
    context.save();
    context.fillStyle = backgroundColor ?? '#ffffff';
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.restore();
  }

  const metrics = getPlacementMetrics(
    image.width,
    image.height,
    targetWidth,
    targetHeight,
    mode,
    zoom,
  );
  const clampedOffsets = clampOffsets(
    image.width,
    image.height,
    targetWidth,
    targetHeight,
    mode,
    zoom,
    offsetX,
    offsetY,
  );
  const drawX = metrics.baseX + clampedOffsets.x;
  const drawY = metrics.baseY + clampedOffsets.y;

  context.drawImage(image.element, drawX, drawY, metrics.renderedWidth, metrics.renderedHeight);
}

type BrowserSocialMediaImageResizerProps = {
  variant?: SocialMediaImageResizerVariant;
};

export default function BrowserSocialMediaImageResizer({
  variant,
}: BrowserSocialMediaImageResizerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointerDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const lockedPreset = useMemo(
    () =>
      variant?.lockedPresetId
        ? (presets.find((preset) => preset.id === variant.lockedPresetId) ?? null)
        : null,
    [variant?.lockedPresetId],
  );
  const visiblePresets = useMemo(() => (lockedPreset ? [lockedPreset] : presets), [lockedPreset]);
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [activePresetId, setActivePresetId] = useState<string>(lockedPreset?.id ?? presets[0].id);
  const [framingMode, setFramingMode] = useState<FramingMode>('crop');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(0.94);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [backgroundPreset, setBackgroundPreset] = useState<BackgroundPreset>('transparent');
  const [customBackgroundColor, setCustomBackgroundColor] = useState('#111827');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPointerDragging, setIsPointerDragging] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [previewFrameSize, setPreviewFrameSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const activePreset = useMemo(
    () => visiblePresets.find((preset) => preset.id === activePresetId) ?? visiblePresets[0],
    [activePresetId, visiblePresets],
  );
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const activeFramingMode = useMemo(
    () => framingModes.find((mode) => mode.value === framingMode) ?? framingModes[0],
    [framingMode],
  );
  const previewSize = useMemo(
    () =>
      getContainSize(
        activePreset.width,
        activePreset.height,
        Math.max(320, (previewFrameSize?.width ?? 1120) - 40),
        Math.max(220, (previewFrameSize?.height ?? 760) - 40),
      ),
    [activePreset.height, activePreset.width, previewFrameSize],
  );
  const effectiveBackground = useMemo(
    () => getEffectiveBackground(backgroundPreset, customBackgroundColor, outputFormat),
    [backgroundPreset, customBackgroundColor, outputFormat],
  );

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  useEffect(() => {
    if (!previewFrameRef.current) {
      return;
    }

    const frame = previewFrameRef.current;
    const syncFrameSize = () => {
      setPreviewFrameSize({
        width: frame.clientWidth,
        height: frame.clientHeight,
      });
    };

    syncFrameSize();

    const observer = new ResizeObserver(() => syncFrameSize());
    observer.observe(frame);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (outputFormat === 'jpeg' && backgroundPreset === 'transparent') {
      setBackgroundPreset('white');
    }
  }, [backgroundPreset, outputFormat]);

  useEffect(() => {
    if (!lockedPreset) {
      return;
    }

    setActivePresetId(lockedPreset.id);
  }, [lockedPreset]);

  useEffect(() => {
    if (!loadedImage) {
      return;
    }

    const nextFrame = getSmartFraming(loadedImage, activePreset, framingMode);
    setZoom(nextFrame.zoom);
    setOffsetX(nextFrame.offsetX);
    setOffsetY(nextFrame.offsetY);
  }, [activePreset, framingMode, loadedImage]);

  useEffect(() => {
    const canvas = previewCanvasRef.current;

    if (!canvas || !loadedImage) {
      return;
    }

    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(previewSize.width * devicePixelRatio);
    canvas.height = Math.round(previewSize.height * devicePixelRatio);

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(devicePixelRatio, devicePixelRatio);

    drawResizedImage(
      context,
      loadedImage,
      previewSize.width,
      previewSize.height,
      framingMode,
      zoom,
      offsetX,
      offsetY,
      effectiveBackground.color,
      outputFormat,
    );
  }, [
    effectiveBackground.color,
    framingMode,
    loadedImage,
    offsetX,
    offsetY,
    outputFormat,
    previewSize.height,
    previewSize.width,
    zoom,
  ]);

  function resetOutputState() {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setDownloadUrl(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);
    setIsLoadingImage(true);

    if (!file) {
      setLoadedImage(null);
      setIsLoadingImage(false);
      return;
    }

    try {
      const image = await loadImageFromFile(file);
      const nextLoadedImage = {
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
      };

      setLoadedImage(nextLoadedImage);
      const nextFrame = getSmartFraming(nextLoadedImage, activePreset, framingMode);
      setZoom(nextFrame.zoom);
      setOffsetX(nextFrame.offsetX);
      setOffsetY(nextFrame.offsetY);
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Could not read this image.');
    } finally {
      setIsLoadingImage(false);
    }
  }

  function handlePresetChange(presetId: string) {
    setActivePresetId(presetId);
    resetOutputState();
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!loadedImage) {
      return;
    }

    resetOutputState();
    pointerDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX,
      offsetY,
    };
    setIsPointerDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (
      !loadedImage ||
      !pointerDragRef.current ||
      pointerDragRef.current.pointerId !== event.pointerId
    ) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const scaleX = activePreset.width / rect.width;
    const scaleY = activePreset.height / rect.height;
    const nextOffsets = clampOffsets(
      loadedImage.width,
      loadedImage.height,
      activePreset.width,
      activePreset.height,
      framingMode,
      zoom,
      pointerDragRef.current.offsetX + (event.clientX - pointerDragRef.current.startX) * scaleX,
      pointerDragRef.current.offsetY + (event.clientY - pointerDragRef.current.startY) * scaleY,
    );

    setOffsetX(nextOffsets.x);
    setOffsetY(nextOffsets.y);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!pointerDragRef.current || pointerDragRef.current.pointerId !== event.pointerId) {
      return;
    }

    pointerDragRef.current = null;
    setIsPointerDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function resetFraming() {
    if (!loadedImage) {
      return;
    }

    const nextFrame = getSmartFraming(loadedImage, activePreset, framingMode);
    setZoom(nextFrame.zoom);
    setOffsetX(nextFrame.offsetX);
    setOffsetY(nextFrame.offsetY);
    resetOutputState();
  }

  async function runExport() {
    if (!loadedImage) {
      setErrorMessage('Choose an image first.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: variant?.toolSlug ?? 'social-media-image-resizer' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const canvas = document.createElement('canvas');
      canvas.width = activePreset.width;
      canvas.height = activePreset.height;

      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the export.');
      }

      drawResizedImage(
        context,
        loadedImage,
        activePreset.width,
        activePreset.height,
        framingMode,
        zoom,
        offsetX,
        offsetY,
        effectiveBackground.color,
        outputFormat,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The export could not finish for this image.');
      }

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }

      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(loadedImage.name)}-${activePreset.id}.${selectedOutput.extension}`;

      setDownloadUrl(nextDownloadUrl);
      setResultSize(blob.size);
      triggerDownload(nextDownloadUrl, nextFileName);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Metadata stripped on export. Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Metadata stripped on export. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Metadata stripped on export.',
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The image could not be exported.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Resize for social platforms in the browser</h2>
          <p>
            {variant?.hidePresetChooser
              ? `Upload one image, frame it for ${activePreset.label}, and export a social-ready file without sending it to a server.`
              : 'Upload one image, pick a preset, drag to frame it cleanly, and export a social-ready file without sending it to a server.'}
          </p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Client-side'}</span>
      </div>

      {variant?.hidePresetChooser ? null : (
        <div className="tool-live-routes" aria-label="Social media presets">
          {visiblePresets.map((preset) => (
            <button
              key={preset.id}
              className={`tool-live-route ${activePresetId === preset.id ? 'tool-live-route-active' : ''}`}
              onClick={() => handlePresetChange(preset.id)}
              type="button"
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{loadedImage ? loadedImage.name : 'Choose an image or drop it here'}</strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Works best with PNG, JPG, JPEG, WebP, GIF, and BMP files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            {variant?.hidePresetChooser ? null : (
              <label className="field">
                <span>Preset</span>
                <select
                  onChange={(event) => handlePresetChange(event.target.value)}
                  value={activePresetId}
                >
                  {visiblePresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {`${preset.label} • ${preset.width} × ${preset.height}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="field">
              <span>Layout</span>
              <select
                onChange={(event) => {
                  setFramingMode(event.target.value as FramingMode);
                  resetOutputState();
                }}
                value={framingMode}
              >
                {framingModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Format</span>
              <select
                onChange={(event) => {
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Background</span>
              <select
                onChange={(event) => {
                  setBackgroundPreset(event.target.value as BackgroundPreset);
                  resetOutputState();
                }}
                value={backgroundPreset}
              >
                {backgroundOptions.map((background) => (
                  <option
                    disabled={outputFormat === 'jpeg' && background.value === 'transparent'}
                    key={background.value}
                    value={background.value}
                  >
                    {background.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-preview tool-editor-preview tool-social-editor-shell">
            <div className="tool-image-editor-header">
              <h3>Preview</h3>
              <div className="tool-image-editor-header-actions">
                <button
                  className="button button-ghost tool-image-editor-revert"
                  disabled={!loadedImage}
                  onClick={resetFraming}
                  type="button"
                >
                  <span>Reset framing</span>
                </button>
              </div>
            </div>
            <div
              ref={previewFrameRef}
              className="tool-live-preview-frame tool-editor-preview-frame tool-social-editor-frame"
            >
              {loadedImage ? (
                <div
                  className="tool-social-editor-stage"
                  aria-label={`${activePreset.label} preview`}
                  style={{
                    aspectRatio: `${previewSize.width} / ${previewSize.height}`,
                    width: `${previewSize.width}px`,
                    height: `${previewSize.height}px`,
                  }}
                >
                  <canvas
                    ref={previewCanvasRef}
                    className={`tool-editor-canvas tool-social-editor-canvas ${isPointerDragging ? 'tool-social-editor-canvas-dragging' : ''}`}
                    onPointerCancel={handlePointerUp}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                  />
                </div>
              ) : (
                <div className="tool-editor-empty">
                  <strong>{isLoadingImage ? 'Preparing preview…' : 'Preview appears here'}</strong>
                  <span>Upload an image to frame it for the selected social preset.</span>
                </div>
              )}
            </div>
            <div className="tool-social-editor-meta">
              <span>{activeFramingMode.hint}</span>
              <span>Drag inside the preview to reposition the image.</span>
            </div>
          </div>

          <div className="tool-live-quality-shell tool-editor-sliders">
            <label className="field tool-live-quality">
              <span>Scale</span>
              <input
                max="2.4"
                min="1"
                onChange={(event) => {
                  setZoom(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={zoom}
              />
              <small>{zoom.toFixed(2)}×</small>
            </label>
            <label className="field tool-live-quality">
              <span>Background tone</span>
              <div className="tool-social-inline-controls">
                <input
                  aria-label="Background color"
                  className="tool-social-color-input"
                  disabled={backgroundPreset !== 'custom'}
                  onChange={(event) => {
                    setCustomBackgroundColor(event.target.value);
                    resetOutputState();
                  }}
                  type="color"
                  value={customBackgroundColor}
                />
                <span className="tool-social-inline-readout">
                  {backgroundPreset === 'custom'
                    ? customBackgroundColor.toUpperCase()
                    : effectiveBackground.label}
                </span>
              </div>
              <small>
                {outputFormat === 'jpeg'
                  ? 'JPG exports always use a solid background.'
                  : 'Transparent works for PNG and WebP. Solid backgrounds help for fit and fill layouts.'}
              </small>
            </label>
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.98"
                min="0.5"
                onChange={(event) => {
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.02"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `Used for ${selectedOutput.label} exports`
                  : 'PNG keeps the clean export without a quality slider'}
              </small>
            </label>
            <label className="field tool-live-quality">
              <span>Canvas</span>
              <input
                readOnly
                type="text"
                value={`${activePreset.width} × ${activePreset.height}`}
              />
              <small>Exported exactly at the selected preset size.</small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!loadedImage || isExporting}
              onClick={() => void runExport()}
              type="button"
            >
              {isExporting ? 'Exporting…' : `Export as ${selectedOutput.label}`}
            </button>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Social-ready presets</h3>
          <div className="tool-mockup-chip-list">
            <span>{activePreset.label}</span>
            <span>{activeFramingMode.label}</span>
            <span>{selectedOutput.label}</span>
            <span>{effectiveBackground.label}</span>
          </div>
          <p>{activePreset.hint}</p>
          <div className="tool-live-panel-note">
            <strong>How it exports</strong>
            <span>
              Processed fully in your browser. Export is rebuilt from canvas, so common source
              metadata does not carry into the new file.
            </span>
          </div>
          {loadedImage ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Type</strong>
                <span>{normalizeSourceLabel(loadedImage.type)}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{`${loadedImage.width} × ${loadedImage.height}`}</span>
              </div>
              <div>
                <strong>Preset</strong>
                <span>{`${activePreset.width} × ${activePreset.height}`}</span>
              </div>
              <div>
                <strong>Framing</strong>
                <span>{activeFramingMode.label}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Result</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>
    </article>
  );
}
